import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MdPlayArrow,
  MdPause,
  MdVolumeUp,
  MdVolumeOff,
  MdThumbUp,
  MdOutlineThumbUp,
  MdThumbDown,
  MdOutlineThumbDown,
  MdOutlinedFlag,
  MdClose,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdCheck,
  MdSend,
  MdFullscreen,
  MdFullscreenExit,
  MdAutorenew,
  MdSubtitles,
  MdOutlineSubtitles,
  MdMoreVert,
} from 'react-icons/md';
import { RiShareForwardLine } from 'react-icons/ri';
import { BiCommentDetail } from 'react-icons/bi';
import { getAvatarColor } from './utils/avatarUtils';
import { getActiveAccount, PRIMARY_VALID_TOKEN, SECONDARY_VALID_TOKEN, getActiveUserToken } from './utils/accountUtils';
import ShareModal from './ShareModal';
import ReportModal from './ReportModal';
import SignInPromptModal from './SignInPromptModal';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import { recordWatchHistoryItem } from './HistoryPage';
import Hls from 'hls.js';
import { API_BASE_URL, USE_LIVE_BACKEND, normalizeMediaUrl } from './config';
import './ShortsPage.css';

// JWT Token for authenticated real-time telemetry actions (valid active session)
const AUTH_TOKEN = PRIMARY_VALID_TOKEN;

function getApiHeaders(withAuth = false, tokenOverride = null) {
  const headers = {
    token: 'c7e993808a344da9d59cfa20fa8b6061',
    devicetype: '0',
    request_type: 'USER',
  };
  if (withAuth) {
    const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true';
    if (!isLoggedOut) {
      const userToken = tokenOverride || getActiveUserToken();
      if (userToken && userToken.length >= 20) {
        headers['authorization'] = `Bearer ${userToken}`;
      }
    }
  }
  return headers;
}

export default function ShortsPage({ userProfile, isLoggedIn, onLogin, initialShortId, onNavigate }) {
  const [shortsList, setShortsList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false); // Default: Short plays WITH sound (unmuted)
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [centerIcon, setCenterIcon] = useState(null); // 'play' | 'pause' | 'volume-up' | 'volume-off'

  // Check if current user is authenticated
  const isUserAuthenticated = useMemo(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true') {
      return false;
    }
    const acc = getActiveAccount() || userProfile;
    if (acc && (acc.id || acc.token || acc.userId || acc.email || acc.name)) {
      return true;
    }
    if (typeof isLoggedIn === 'boolean') return isLoggedIn;
    const token = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_token') : null;
    const account = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_account') : null;
    return Boolean(token && account && localStorage.getItem('speedotube_logged_out') !== 'true');
  }, [isLoggedIn, userProfile]);

  // Smooth Sign-In Prompt Modal State
  const [signInPrompt, setSignInPrompt] = useState({
    isOpen: false,
    action: 'general',
    targetName: '',
  });

  const openSignInPrompt = useCallback((action = 'general', targetName = '') => {
    setSignInPrompt({
      isOpen: true,
      action,
      targetName,
    });
  }, []);

  const closeSignInPrompt = useCallback(() => {
    setSignInPrompt((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handlePromptSignIn = useCallback(() => {
    closeSignInPrompt();
    if (onLogin) {
      onLogin();
    } else if (onNavigate) {
      onNavigate('/auth');
    }
  }, [closeSignInPrompt, onLogin, onNavigate]);

  // Vertical Swipe Transition States & Feedback
  const [transitionState, setTransitionState] = useState(null); // { direction: 'up' | 'down', outgoingShort, incomingShort }
  const [bounceClass, setBounceClass] = useState('');
  const isTransitioningRef = useRef(false);
  const touchStartY = useRef(null);

  const triggerBounce = useCallback((dir) => {
    setBounceClass(dir === 'up' ? 'yt-bounce-up' : 'yt-bounce-down');
    setTimeout(() => setBounceClass(''), 320);
  }, []);

  // Clear any legacy muted localStorage state so sound is always enabled by default
  useEffect(() => {
    try {
      localStorage.removeItem('speedotube_shorts_muted');
    } catch (e) {}
  }, []);

  // Interaction States for the current short
  const [likeStates, setLikeStates] = useState({}); // { [shortId]: { isLiked, isDisliked, likeCount } }
  const [subscribedChannels, setSubscribedChannels] = useState({}); // { [channelHandle]: boolean }
  const [commentsMap, setCommentsMap] = useState({}); // { [shortId]: [comments] }

  // Modals & Panels
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // YouTube Shorts Feature Toggles (Fullscreen, Autoscroll Mode, CC, Options Menu)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoscroll, setIsAutoscroll] = useState(() => {
    try {
      return localStorage.getItem('speedotube_shorts_autoscroll') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [isCaptionsOn, setIsCaptionsOn] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const playerCardRef = useRef(null);
  const centerIconTimeoutRef = useRef(null);
  const viewedShortsRef = useRef(new Set());
  const currentPlayingShortRef = useRef(null);
  const accumulatedWatchSecondsRef = useRef(0);
  const lastPlaybackTimestampRef = useRef(null);

  const [activeUser, setActiveUser] = useState(() => getActiveAccount() || userProfile || {});

  useEffect(() => {
    if (userProfile) {
      setActiveUser(userProfile);
    }
  }, [userProfile]);

  const activeAccount = getActiveAccount() || (activeUser?.id ? activeUser : (userProfile || {}));
  const currentShort = shortsList[currentIndex] || null;
  const currentShortId = currentShort ? String(currentShort.id || currentShort.video_url_id) : null;

  // Dynamic Creator Detection: True if the logged-in viewer is the creator/uploader of this Short
  const isOwner = useMemo(() => {
    if (!currentShort) return false;
    const currentUserAccount = getActiveAccount() || activeAccount;
    if (!currentUserAccount) return false;

    const currentUserId = currentUserAccount?.id || currentUserAccount?.userId || currentUserAccount?.legacyId;
    const currentLegacyId = currentUserAccount?.legacyId;
    const currentChannelId = currentUserAccount?.channelId || currentUserAccount?.channel_id;
    const currentName = (currentUserAccount?.name || currentUserAccount?.fullName || '').trim().toLowerCase();
    const currentHandle = (currentUserAccount?.handle || currentUserAccount?.handleName || currentUserAccount?.username || '')
      .trim()
      .toLowerCase()
      .replace(/^@/, '');

    const shortUserId = currentShort?.user_id || currentShort?.userId;
    const shortChannelId = currentShort?.channel_id || currentShort?.channelId;
    const shortChannelName = (currentShort?.channel_name || currentShort?.channelName || '').trim().toLowerCase();
    const shortHandle = (currentShort?.handle_name || currentShort?.channelHandle || '')
      .trim()
      .toLowerCase()
      .replace(/^@/, '');

    // Match by User ID or Legacy ID
    if (currentUserId && shortUserId && String(currentUserId) === String(shortUserId)) return true;
    if (currentLegacyId && shortUserId && String(currentLegacyId) === String(shortUserId)) return true;

    // Match by Channel ID
    if (currentChannelId && shortChannelId && String(currentChannelId) === String(shortChannelId)) return true;

    // Match by Channel handle or name
    if (currentHandle && shortHandle && currentHandle === shortHandle) return true;
    if (currentName && shortChannelName && currentName === shortChannelName) return true;

    // Known account mappings
    if (
      (currentChannelId === 40 || currentHandle === 'aukatdikhadi' || currentUserId === 198 || currentUserId === 3) &&
      (shortChannelId === 40 || shortHandle === 'aukatdikhadi' || shortUserId === 3 || shortUserId === 198)
    ) {
      return true;
    }
    if (
      (currentChannelId === 38 || currentHandle === 'pratham' || currentUserId === 147 || currentUserId === 1) &&
      (shortChannelId === 38 || shortHandle === 'pratham' || shortUserId === 1 || shortUserId === 147)
    ) {
      return true;
    }

    return false;
  }, [currentShort, activeAccount]);

  // 1. Fetch Shorts List from Backend
  const fetchShorts = useCallback(async () => {
    try {
      const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true';
      const curAcc = !isLoggedOut ? (getActiveAccount() || userProfile) : null;
      const curToken = curAcc ? (curAcc.token || getActiveUserToken(curAcc)) : null;
      const res = await fetch(`${API_BASE_URL}/api/v1/shorts?limit=25&offset=0`, {
        headers: getApiHeaders(Boolean(!isLoggedOut && curToken), curToken),
      });
      if (res.ok) {
        const json = await res.json();
        const rawVideos = json?.data?.videos || [];

        // Ensure current Short 195 is included with its real video_url and thumbnail
        let formatted = rawVideos.map((v) => ({
          ...v,
          id: v.id,
          videoUrlId: v.video_url_id,
          userId: v.user_id || v.userId,
          channelId: v.channel_id || v.channelId,
          title: v.title || 'Untitled Short',
          channelName: v.channel_name || 'Creator',
          channelHandle: v.handle_name ? (v.handle_name.startsWith('@') ? v.handle_name : `@${v.handle_name}`) : '@creator',
          videoUrl: v.video_url ? normalizeMediaUrl(v.video_url.replace(/h264\.mpd$/i, 'h264.m3u8')) : '',
          thumbnailUrl: normalizeMediaUrl(v.thumbnail_url) || '',
          likes: parseInt(v.like_count || 0, 10),
          dislikes: parseInt(v.dislike_count || 0, 10),
          commentCount: parseInt(v.comment_count || 0, 10),
          userLiked: Boolean(v.user_liked),
          userDisliked: Boolean(v.user_disliked),
          userSubscribed: Boolean(v.user_subscribed),
        }));

        // Backfill only if running strictly on local offline backend with 0 shorts returned
        const isLiveMode = USE_LIVE_BACKEND || API_BASE_URL.includes('speedoprime.com') || API_BASE_URL.includes('103.88.81.5') || API_BASE_URL.includes('103.88.81.7') || API_BASE_URL === '';
        if (!isLiveMode && formatted.length === 0) {
          formatted.unshift({
            id: 195,
            videoUrlId: 'JpRDutcHYCj',
            userId: 3,
            channelId: 40,
            title: 'Chale Aana song Armaan Malik #armaanmalik #shorts #ytshorts #comment #subscribe 1080p',
            channelName: 'Armaan Malik',
            channelHandle: '@armaanmalik',
            videoUrl: normalizeMediaUrl('/drive/videos/short_195_chale_aana.mp4'),
            thumbnailUrl: normalizeMediaUrl('/drive/thumbnails/short_194_chale_aana.jpg'),
            likes: 0,
            dislikes: 0,
            commentCount: 0,
            userLiked: false,
            userDisliked: false,
            userSubscribed: false,
          });
        }

        // Only keep the 2 requested Shorts: 'Saanu Saariyan' (id 182) and 'GT Bike' (id 209)
        const ALLOWED_SHORT_IDS = ['182', '209', 182, 209];
        const filteredShorts = formatted.filter((s) => {
          if (ALLOWED_SHORT_IDS.includes(s.id)) return true;
          const t = (s.title || '').toLowerCase();
          return t.includes('saanu') || t.includes('gt bike');
        });

        setShortsList(filteredShorts);

        // Pre-populate likeStates directly from the fresh server response
        const initialLikeStates = {};
        filteredShorts.forEach((s) => {
          initialLikeStates[s.id] = {
            isLiked: Boolean(s.userLiked),
            isDisliked: Boolean(s.userDisliked),
            likeCount: s.likes || 0,
          };
        });
        setLikeStates(initialLikeStates);

        // If an initialShortId was passed, find its index
        if (initialShortId) {
          const foundIdx = formatted.findIndex(
            (s) => String(s.id) === String(initialShortId) || s.videoUrlId === String(initialShortId)
          );
          if (foundIdx !== -1) {
            setCurrentIndex(foundIdx);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to fetch shorts from backend:', e);
    }
  }, [initialShortId]);

  useEffect(() => {
    fetchShorts();
  }, [fetchShorts]);

  useEffect(() => {
    const handleAccountSwitch = (e) => {
      const switched = e?.detail || getActiveAccount() || userProfile || {};
      setActiveUser(switched);
      setLikeStates({});
      fetchShorts();
    };
    window.addEventListener('speedotube_user_switched', handleAccountSwitch);
    return () => window.removeEventListener('speedotube_user_switched', handleAccountSwitch);
  }, [userProfile, fetchShorts]);

  // Sync likes and subscription state when currentShort changes
  useEffect(() => {
    if (!currentShortId || !currentShort) return;

    setLikeStates((prev) => ({
      ...prev,
      [currentShortId]: {
        isLiked: Boolean(currentShort.userLiked),
        isDisliked: Boolean(currentShort.userDisliked),
        likeCount: currentShort.likes || 0,
      },
    }));

    const handle = currentShort.channelHandle;
    if (handle) {
      setSubscribedChannels((prev) => ({
        ...prev,
        [handle]: Boolean(currentShort.userSubscribed),
      }));
    }

    // Auto-fetch comments for current short
    fetchCommentsForShort(currentShort.id);
  }, [currentShortId, currentShort?.likes, currentShort?.userLiked, currentShort?.userSubscribed, currentShort?.channelHandle]);

  // Synchronize browser tab title and URL dynamically with active Short
  useEffect(() => {
    if (currentShort?.title) {
      document.title = `${currentShort.title} - SpeedoTube`;
    }
    const shortParam = currentShort?.videoUrlId || currentShort?.id;
    if (shortParam && window.location.pathname.startsWith('/shorts')) {
      const targetUrl = `/shorts/${shortParam}`;
      if (window.location.pathname !== targetUrl) {
        window.history.replaceState({ path: targetUrl }, '', targetUrl);
      }
    }
  }, [currentShort?.id, currentShort?.videoUrlId, currentShort?.title]);

  // Attach HLS stream (.m3u8) or direct video to shorts video element
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !currentShort?.videoUrl) return;

    const url = currentShort.videoUrl;
    const isHls = typeof url === 'string' && url.includes('.m3u8');

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Native DOM ratechange guard — fires synchronously before React's synthetic event,
    // ensuring HLS.js LatencyController / live catchup can never accelerate playback.
    const forceNormalRate = () => {
      if (videoEl && videoEl.playbackRate !== 1.0) {
        videoEl.playbackRate = 1.0;
        videoEl.defaultPlaybackRate = 1.0;
      }
    };
    videoEl.addEventListener('ratechange', forceNormalRate);
    videoEl.addEventListener('play', forceNormalRate);

    const clampRate = () => {
      if (videoEl) {
        videoEl.playbackRate = 1.0;
        videoEl.defaultPlaybackRate = 1.0;
      }
    };

    if (isHls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        // Prevent LatencyController from ever accelerating playback to catch up with live edge
        maxLiveSyncPlaybackRate: 1,
        liveSyncDurationCount: 10,
        liveMaxLatencyDurationCount: Infinity,
      });

      hls.loadSource(url);
      hls.attachMedia(videoEl);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Clamp rate only — play() is driven exclusively by the play/pause useEffect
        clampRate();
      });

      // Clamp on every fragment boundary — HLS.js may adjust rate between segments
      hls.on(Hls.Events.FRAG_CHANGED, () => clampRate());
      hls.on(Hls.Events.LEVEL_LOADED, () => clampRate());

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.warn('Shorts HLS stream error:', data);
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              hlsRef.current = null;
              break;
          }
        }
      });

      hlsRef.current = hls;
    } else if (isHls && videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS (Safari) — just load; play() is driven by the play/pause useEffect
      clampRate();
      videoEl.src = url;
      videoEl.load();
    } else {
      // Plain MP4 / other — just load; play() is driven by the play/pause useEffect
      clampRate();
      videoEl.src = url;
      videoEl.load();
    }

    return () => {
      videoEl.removeEventListener('ratechange', forceNormalRate);
      videoEl.removeEventListener('play', forceNormalRate);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [currentShort?.id, currentShort?.videoUrl]);

  // Keep like states and shorts list in sync on like events
  useEffect(() => {
    const handleVideoLikeChanged = (e) => {
      const { videoId, user_liked, user_disliked, like_count } = e.detail || {};
      if (!videoId) return;
      setLikeStates((prev) => ({
        ...prev,
        [videoId]: {
          isLiked: user_liked === 1 || user_liked === true,
          isDisliked: user_disliked === 1 || user_disliked === true,
          likeCount: like_count !== undefined ? like_count : prev[videoId]?.likeCount || 0,
        },
      }));
      setShortsList((prev) =>
        prev.map((s) =>
          String(s.id) === String(videoId)
            ? {
                ...s,
                userLiked: user_liked === 1 || user_liked === true,
                userDisliked: user_disliked === 1 || user_disliked === true,
                likes: like_count !== undefined ? like_count : s.likes,
              }
            : s
        )
      );
    };

    window.addEventListener('speedotube:video-like-changed', handleVideoLikeChanged);
    return () => {
      window.removeEventListener('speedotube:video-like-changed', handleVideoLikeChanged);
    };
  }, []);

  // Fetch comments for a specific short
  const fetchCommentsForShort = async (videoId) => {
    if (!videoId) return;
    try {
      const curAcc = activeAccount || getActiveAccount();
      const curToken = curAcc?.token || getActiveUserToken(curAcc);
      const res = await fetch(`${API_BASE_URL}/api/v1/comments/videos/${videoId}`, {
        headers: getApiHeaders(Boolean(isUserAuthenticated), curToken),
      });
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json?.data) ? json.data : (json?.data?.comments || []);
        setCommentsMap((prev) => ({
          ...prev,
          [videoId]: list,
        }));
        const count = json?.data?.comment_count !== undefined ? json.data.comment_count : list.length;
        setShortsList((prev) =>
          prev.map((s) => (String(s.id) === String(videoId) ? { ...s, commentCount: count } : s))
        );
      }
    } catch (e) {
      console.warn('Comments fetch error:', e);
    }
  };

  // ─── Single source of truth for play / pause ────────────────────────────────
  // ONLY this effect drives videoEl.play() / .pause() so that onCanPlay,
  // MANIFEST_PARSED, and the useEffect never fight each other.
  // NOTE: isMuted is intentionally NOT in the dep-array — muting is applied
  // directly on the DOM node and must not re-trigger a play() / pause() cycle.
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (isPlaying) {
      // Always start muted so browser autoplay policy never blocks us.
      // We'll unmute on first user interaction (see enableAudio below).
      const tryPlay = () => {
        videoEl.muted = isMuted || videoEl.muted; // keep user mute choice if already set
        const promise = videoEl.play();
        if (promise !== undefined) {
          promise.catch((err) => {
            if (err.name === 'NotAllowedError') {
              // Browser blocked unmuted play — fall back to muted
              if (videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.play().catch(() => {});
              }
            }
            // Ignore AbortError — happens when a new load cancels a pending play()
          });
        }
      };
      tryPlay();
    } else {
      videoEl.pause();
    }
  }, [isPlaying, currentIndex, currentShortId]);

  // Restore unmuted sound on first user interaction with the page
  useEffect(() => {
    const enableAudio = () => {
      if (videoRef.current && !isMuted) {
        videoRef.current.muted = false;
        // Do NOT call play() here — the video should already be playing
      }
    };
    window.addEventListener('pointerdown', enableAudio, { once: true });
    window.addEventListener('click', enableAudio, { once: true });
    window.addEventListener('keydown', enableAudio, { once: true });
    window.addEventListener('wheel', enableAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', enableAudio);
      window.removeEventListener('click', enableAudio);
      window.removeEventListener('keydown', enableAudio);
      window.removeEventListener('wheel', enableAudio);
    };
  }, [isMuted]);

  // Show center icon animation helper
  const triggerCenterIcon = (type) => {
    setCenterIcon(type);
    if (centerIconTimeoutRef.current) clearTimeout(centerIconTimeoutRef.current);
    centerIconTimeoutRef.current = setTimeout(() => {
      setCenterIcon(null);
    }, 600);
  };

  // Toggle Play / Pause
  const handleTogglePlay = () => {
    setIsPlaying((prev) => {
      const next = !prev;
      triggerCenterIcon(next ? 'play' : 'pause');
      return next;
    });
  };

  // Toggle Mute / Unmute
  const handleToggleMute = (e) => {
    if (e) e.stopPropagation();
    setIsMuted((prev) => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      triggerCenterIcon(next ? 'volume-off' : 'volume-up');
      return next;
    });
  };

  // Toggle Fullscreen Mode
  const handleToggleFullscreen = (e) => {
    if (e) e.stopPropagation();
    const targetEl = containerRef.current || playerCardRef.current;
    if (!targetEl) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (targetEl.requestFullscreen) {
        targetEl.requestFullscreen().catch(() => {});
      } else if (targetEl.webkitRequestFullscreen) {
        targetEl.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
      triggerCenterIcon('fullscreen');
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      setIsFullscreen(false);
      triggerCenterIcon('fullscreen-exit');
    }
  };

  // Listen for native Fullscreen changes (e.g. Esc key)
  useEffect(() => {
    const handleFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(Boolean(fsEl));
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
    };
  }, []);

  // Toggle Autoscroll Mode
  const handleToggleAutoscroll = (e) => {
    if (e) e.stopPropagation();
    setIsAutoscroll((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('speedotube_shorts_autoscroll', next ? 'true' : 'false');
      } catch (err) {}
      triggerCenterIcon(next ? 'autoscroll-on' : 'autoscroll-off');
      return next;
    });
    setShowMoreMenu(false);
  };

  // Toggle Closed Captions / Subtitles
  const handleToggleCaptions = (e) => {
    if (e) e.stopPropagation();
    setIsCaptionsOn((prev) => {
      const next = !prev;
      triggerCenterIcon(next ? 'captions-on' : 'captions-off');
      return next;
    });
  };

  // Close More Options popup on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [showMoreMenu]);

  // Commit watch time telemetry slice to backend API (v_watch_time)
  const commitWatchTime = useCallback((targetShortId) => {
    const vidId = targetShortId || currentPlayingShortRef.current?.id;
    if (!vidId) return;

    // Roll in any active elapsed time slice since last tick
    if (lastPlaybackTimestampRef.current !== null) {
      const now = performance.now();
      const elapsed = (now - lastPlaybackTimestampRef.current) / 1000;
      if (elapsed > 0 && elapsed < 5.0) {
        accumulatedWatchSecondsRef.current += elapsed;
      }
      lastPlaybackTimestampRef.current = now;
    }

    const durationSec = accumulatedWatchSecondsRef.current;
    // Don't spam backend for micro-slices < 0.5s
    if (durationSec < 0.5) return;

    // Reset accumulator for next interval
    accumulatedWatchSecondsRef.current = 0;

    const nowWall = Date.now();
    const durRounded = Math.round(durationSec * 10) / 10;
    const curMediaSec = videoRef.current ? Math.round(videoRef.current.currentTime || 0) : 0;
    const startSec = Math.max(0, curMediaSec - Math.round(durRounded));
    const endSec = curMediaSec;

    const payload = JSON.stringify({
      video_id: Number(vidId),
      start_time: new Date(nowWall - Math.round(durRounded * 1000)).toISOString(),
      end_time: new Date(nowWall).toISOString(),
      start_sec: startSec,
      end_sec: endSec,
    });

    try {
      fetch(`${API_BASE_URL}/api/v1/watch-time`, {
        method: 'POST',
        headers: {
          ...getApiHeaders(true),
          'Content-Type': 'application/json',
        },
        body: payload,
        keepalive: true,
      })
        .then((res) => {
          if (res.ok) {
            window.dispatchEvent(
              new CustomEvent('speedotube:watch-time-updated', {
                detail: { videoId: Number(vidId), watchSeconds: durRounded },
              })
            );
          }
        })
        .catch((err) => console.warn('Shorts watch time error:', err));
    } catch (e) {
      console.warn('Shorts watch time fetch failed:', e);
    }
  }, []);

  // Handle Play event from video DOM element
  const handlePlay = useCallback(() => {
    lastPlaybackTimestampRef.current = performance.now();
    setIsPlaying(true);
  }, []);

  // Handle Pause event from video DOM element.
  // IMPORTANT: only update React state if this was a genuine user-triggered pause.
  // If the browser pauses due to autoplay policy (state still says isPlaying=true),
  // we do NOT set isPlaying=false — that would trigger the useEffect to call pause()
  // creating a feedback loop. We just track watch time and let the useEffect retry.
  const handlePause = useCallback(() => {
    if (lastPlaybackTimestampRef.current !== null) {
      const elapsed = (performance.now() - lastPlaybackTimestampRef.current) / 1000;
      if (elapsed > 0 && elapsed < 5.0) {
        accumulatedWatchSecondsRef.current += elapsed;
      }
      lastPlaybackTimestampRef.current = null;
    }
    // Only reflect the pause in React state if it matches what we intended.
    // If isPlayingRef.current is true but the video paused, that was browser-forced — ignore it.
    if (!isPlayingRef.current) {
      setIsPlaying(false);
    }
  }, []);

  // Sync current playing short ref and commit on change/unmount
  useEffect(() => {
    if (!currentShort) return;
    const prevId = currentPlayingShortRef.current?.id;
    if (prevId && prevId !== currentShort.id) {
      commitWatchTime(prevId);
    }
    currentPlayingShortRef.current = currentShort;
    accumulatedWatchSecondsRef.current = 0;
    lastPlaybackTimestampRef.current = performance.now();

    return () => {
      if (currentShort?.id) {
        commitWatchTime(currentShort.id);
      }
    };
  }, [currentShort?.id, commitWatchTime]);

  // Flush pending watch time on tab visibility change or window unload
  useEffect(() => {
    const handleFlush = () => {
      if (currentPlayingShortRef.current?.id) {
        commitWatchTime(currentPlayingShortRef.current.id);
      }
    };
    window.addEventListener('beforeunload', handleFlush);
    window.addEventListener('pagehide', handleFlush);
    const handleVis = () => {
      if (document.hidden) {
        handleFlush();
      } else {
        lastPlaybackTimestampRef.current = performance.now();
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => {
      window.removeEventListener('beforeunload', handleFlush);
      window.removeEventListener('pagehide', handleFlush);
      document.removeEventListener('visibilitychange', handleVis);
    };
  }, [commitWatchTime]);

  // Time & Progress Update with continuous view & watch-time recording
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const cur = videoRef.current.currentTime || 0;
    const dur = videoRef.current.duration || 0;
    setCurrentTime(cur);
    setDuration(dur);

    // Auto-scroll when short playback reaches the end
    if (isAutoscroll && isPlaying && !isTransitioningRef.current && dur > 1) {
      if (cur >= dur - 0.28 || videoRef.current.ended) {
        handleNextShort();
        return;
      }
    }

    // 1. Accumulate continuous playback seconds (even across loops)
    if (!videoRef.current.paused && !videoRef.current.seeking) {
      const now = performance.now();
      if (lastPlaybackTimestampRef.current !== null) {
        const delta = (now - lastPlaybackTimestampRef.current) / 1000;
        // Count continuous forward playback slices (up to 2.5s)
        if (delta > 0 && delta < 2.5) {
          accumulatedWatchSecondsRef.current += delta;
        }
      }
      lastPlaybackTimestampRef.current = now;

      // Periodically commit every 5 seconds of accumulated watch time
      if (accumulatedWatchSecondsRef.current >= 5.0 && currentShort) {
        commitWatchTime(currentShort.id);
      }
    }

    // 2. Legitimate View & Watch History registration at >= 2.5s
    if (currentShort && cur >= 2.5 && !viewedShortsRef.current.has(currentShort.id)) {
      viewedShortsRef.current.add(currentShort.id);
      fetch(`${API_BASE_URL}/api/v1/videos/${currentShort.id}/view`, {
        method: 'POST',
        headers: getApiHeaders(true),
      })
        .then(() => {
          window.dispatchEvent(
            new CustomEvent('speedotube:video-view-incremented', {
              detail: { videoId: currentShort.id },
            })
          );
        })
        .catch((err) => console.warn('Short view increment error:', err));

      try {
        recordWatchHistoryItem({
          id: currentShort.id,
          title: currentShort.title,
          channelName: currentShort.channelName || currentShort.channelHandle || 'SpeedoTube Creator',
          channelAvatar: currentShort.avatarUrl || '',
          views: currentShort.views ? `${currentShort.views} views` : '1 view',
          duration: `${Math.round(videoRef.current?.duration || 0)}s`,
          thumbnailUrl: currentShort.thumbnailUrl,
          type: 'shorts',
        });
      } catch (e) {
        console.warn('Failed to record shorts history item:', e);
      }
    }
  };

  // Interactive Draggable Scrubber Logic (Click and Drag across the red line)
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubPreviewTime, setScrubPreviewTime] = useState(null);
  const scrubberTrackRef = useRef(null);
  const wasPlayingBeforeScrubRef = useRef(false);

  const calculateScrubTime = useCallback((clientX) => {
    if (!scrubberTrackRef.current || !duration) return 0;
    const rect = scrubberTrackRef.current.getBoundingClientRect();
    const pos = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const ratio = rect.width > 0 ? pos / rect.width : 0;
    return ratio * duration;
  }, [duration]);

  const handleScrubberPointerDown = (e) => {
    e.stopPropagation();
    if (!videoRef.current || !duration) return;
    
    // Capture pointer so dragging continues even outside the element
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_) {}

    setIsScrubbing(true);
    wasPlayingBeforeScrubRef.current = !videoRef.current.paused;
    if (wasPlayingBeforeScrubRef.current) {
      videoRef.current.pause();
    }

    const seekTime = calculateScrubTime(e.clientX);
    setScrubPreviewTime(seekTime);
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleScrubberPointerMove = (e) => {
    if (!isScrubbing || !videoRef.current || !duration) return;
    e.stopPropagation();
    const seekTime = calculateScrubTime(e.clientX);
    setScrubPreviewTime(seekTime);
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleScrubberPointerUp = (e) => {
    if (!isScrubbing) return;
    e.stopPropagation();
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {}

    const seekTime = calculateScrubTime(e.clientX);
    if (videoRef.current && duration) {
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
      if (wasPlayingBeforeScrubRef.current) {
        videoRef.current.play().catch(() => {});
      }
    }
    setIsScrubbing(false);
    setScrubPreviewTime(null);
  };

  // Navigation: Next & Prev with Smooth Vertical Swipe Transitions
  const handleNextShort = useCallback(() => {
    if (isTransitioningRef.current) return;
    if (currentIndex < shortsList.length - 1) {
      if (currentShort) {
        commitWatchTime(currentShort.id);
      }
      const nextIdx = currentIndex + 1;
      isTransitioningRef.current = true;
      setTransitionState({
        direction: 'up',
        outgoingShort: shortsList[currentIndex],
        incomingShort: shortsList[nextIdx],
      });
      setCurrentIndex(nextIdx);
      setIsPlaying(true);
      setCurrentTime(0);

      setTimeout(() => {
        setTransitionState(null);
        isTransitioningRef.current = false;
      }, 380);
    } else if (isAutoscroll && shortsList.length > 1) {
      if (currentShort) {
        commitWatchTime(currentShort.id);
      }
      const nextIdx = 0;
      isTransitioningRef.current = true;
      setTransitionState({
        direction: 'up',
        outgoingShort: shortsList[currentIndex],
        incomingShort: shortsList[nextIdx],
      });
      setCurrentIndex(nextIdx);
      setIsPlaying(true);
      setCurrentTime(0);

      setTimeout(() => {
        setTransitionState(null);
        isTransitioningRef.current = false;
      }, 380);
    } else {
      triggerBounce('up');
    }
  }, [currentIndex, currentShort, shortsList, isAutoscroll, triggerBounce, commitWatchTime]);

  const handlePrevShort = useCallback(() => {
    if (isTransitioningRef.current) return;
    if (currentIndex > 0) {
      if (currentShort) {
        commitWatchTime(currentShort.id);
      }
      const prevIdx = currentIndex - 1;
      isTransitioningRef.current = true;
      setTransitionState({
        direction: 'down',
        outgoingShort: shortsList[currentIndex],
        incomingShort: shortsList[prevIdx],
      });
      setCurrentIndex(prevIdx);
      setIsPlaying(true);
      setCurrentTime(0);

      setTimeout(() => {
        setTransitionState(null);
        isTransitioningRef.current = false;
      }, 380);
    } else {
      triggerBounce('down');
    }
  }, [currentIndex, currentShort, shortsList, triggerBounce, commitWatchTime]);

  // Touch swipe gesture handlers (for mobile & touch devices)
  const handleTouchStart = (e) => {
    if (e.touches && e.touches[0]) {
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const endY = e.changedTouches[0].clientY;
    const diff = touchStartY.current - endY;
    if (diff > 45) {
      handleNextShort(); // Swiped up -> Next
    } else if (diff < -45) {
      handlePrevShort(); // Swiped down -> Prev
    }
    touchStartY.current = null;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't capture keys if typing inside a textarea or input
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        handleNextShort();
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        handlePrevShort();
      } else if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        handleTogglePlay();
      } else if (e.key === 'm') {
        e.preventDefault();
        handleToggleMute();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        handleToggleFullscreen();
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        handleToggleCaptions();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNextShort, handlePrevShort]);

  // Wheel scroll navigation (smooth throttle)
  const lastScrollTime = useRef(0);
  const handleWheel = (e) => {
    const now = Date.now();
    if (now - lastScrollTime.current < 550) return;
    if (e.deltaY > 30) {
      lastScrollTime.current = now;
      handleNextShort();
    } else if (e.deltaY < -30) {
      lastScrollTime.current = now;
      handlePrevShort();
    }
  };

  // Interactions: Like / Dislike
  const currentLikeState = useMemo(() => {
    if (currentShortId && likeStates[currentShortId]) {
      return likeStates[currentShortId];
    }
    return {
      isLiked: Boolean(currentShort?.userLiked),
      isDisliked: Boolean(currentShort?.userDisliked),
      likeCount: currentShort?.likes || 0,
    };
  }, [currentShortId, likeStates, currentShort]);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentShort) return;
    if (!isUserAuthenticated) {
      openSignInPrompt('like', currentShort.title || '');
      return;
    }

    const shortId = currentShort.id;
    const existing = likeStates[shortId] || {
      isLiked: Boolean(currentShort.userLiked),
      isDisliked: Boolean(currentShort.userDisliked),
      likeCount: currentShort.likes || 0,
    };
    const nextLiked = !existing.isLiked;
    const countDelta = nextLiked ? 1 : (existing.isLiked ? -1 : 0);
    const newLikeCount = Math.max(0, existing.likeCount + countDelta);

    const curAcc = getActiveAccount() || activeAccount;
    const curToken = curAcc?.token || getActiveUserToken(curAcc);

    // Optimistically update both likeStates and shortsList
    setLikeStates((prev) => ({
      ...prev,
      [shortId]: {
        isLiked: nextLiked,
        isDisliked: false,
        likeCount: newLikeCount,
      },
    }));

    setShortsList((prev) =>
      prev.map((s) =>
        String(s.id) === String(shortId)
          ? {
              ...s,
              userLiked: nextLiked,
              userDisliked: false,
              likes: newLikeCount,
            }
          : s
      )
    );

    // Call real backend API
    try {
      let headers = {
        ...getApiHeaders(true, curToken),
        'Content-Type': 'application/json',
      };
      let res = await fetch(`${API_BASE_URL}/api/v1/videos/${shortId}/like`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ like_dislike: nextLiked ? 'like' : 'unlike' }),
      });

      if (res.status === 401) {
        const freshToken = curToken || getActiveUserToken(curAcc);
        if (freshToken) {
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            'Content-Type': 'application/json',
          };
          res = await fetch(`${API_BASE_URL}/api/v1/videos/${shortId}/like`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ like_dislike: nextLiked ? 'like' : 'unlike' }),
          });
        }
      }

      if (res.ok) {
        const json = await res.json();
        let serverLikes = json?.data?.like_count;
        let serverLiked = json?.data?.user_liked !== undefined ? Boolean(json.data.user_liked) : nextLiked;

        // If live production backend returns without like_count, query video details
        if (serverLikes === undefined) {
          try {
            const vidRes = await fetch(`${API_BASE_URL}/api/v1/videos/${shortId}`, {
              headers: getApiHeaders(true, curToken),
            });
            if (vidRes.ok) {
              const vidJson = await vidRes.json();
              if (vidJson?.data?.video?.like_count !== undefined) {
                serverLikes = parseInt(vidJson.data.video.like_count, 10);
              }
              if (vidJson?.data?.video?.user_liked !== undefined) {
                serverLiked = Boolean(vidJson.data.video.user_liked);
              }
            }
          } catch (_) {}
        }

        const finalLikeCount = serverLikes !== undefined ? serverLikes : newLikeCount;
        setLikeStates((prev) => ({
          ...prev,
          [shortId]: {
            isLiked: serverLiked,
            isDisliked: false,
            likeCount: finalLikeCount,
          },
        }));
        setShortsList((prev) =>
          prev.map((s) =>
            String(s.id) === String(shortId)
              ? {
                  ...s,
                  userLiked: serverLiked,
                  userDisliked: false,
                  likes: finalLikeCount,
                }
              : s
          )
        );
      }

      window.dispatchEvent(
        new CustomEvent('speedotube:video-like-changed', {
          detail: {
            videoId: shortId,
            user_liked: nextLiked ? 1 : 0,
            user_disliked: 0,
            like_count: newLikeCount,
          },
        })
      );
    } catch (err) {
      console.warn('Short like request error:', err);
    }
  };

  const handleDislike = async (e) => {
    e.stopPropagation();
    if (!currentShort) return;
    if (!isUserAuthenticated) {
      openSignInPrompt('dislike', currentShort.title || '');
      return;
    }

    const shortId = currentShort.id;
    const existing = likeStates[shortId] || {
      isLiked: currentShort.userLiked || false,
      isDisliked: currentShort.userDisliked || false,
      likeCount: currentShort.likes || 0,
    };
    const nextDisliked = !existing.isDisliked;
    const countDelta = existing.isLiked ? -1 : 0;
    const newLikeCount = Math.max(0, existing.likeCount + countDelta);

    const curAcc = getActiveAccount() || activeAccount;
    const curToken = curAcc?.token || getActiveUserToken(curAcc);

    setLikeStates((prev) => ({
      ...prev,
      [shortId]: {
        isLiked: false,
        isDisliked: nextDisliked,
        likeCount: newLikeCount,
      },
    }));

    setShortsList((prev) =>
      prev.map((s) =>
        String(s.id) === String(shortId)
          ? {
              ...s,
              userLiked: false,
              userDisliked: nextDisliked,
              likes: newLikeCount,
            }
          : s
      )
    );

    // Call real backend API
    try {
      let headers = {
        ...getApiHeaders(true, curToken),
        'Content-Type': 'application/json',
      };
      let res = await fetch(`${API_BASE_URL}/api/v1/videos/${shortId}/dislike`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ like_dislike: nextDisliked ? 'dislike' : 'undislike' }),
      });

      if (res.status === 401) {
        const freshToken = curToken || getActiveUserToken(curAcc);
        if (freshToken) {
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            'Content-Type': 'application/json',
          };
          res = await fetch(`${API_BASE_URL}/api/v1/videos/${shortId}/dislike`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ like_dislike: nextDisliked ? 'dislike' : 'undislike' }),
          });
        }
      }

      if (res.ok) {
        const json = await res.json();
        let serverLikes = json?.data?.like_count;
        let serverDisliked = json?.data?.user_disliked !== undefined ? Boolean(json.data.user_disliked) : nextDisliked;

        if (serverLikes === undefined) {
          try {
            const vidRes = await fetch(`${API_BASE_URL}/api/v1/videos/${shortId}`, {
              headers: getApiHeaders(true, curToken),
            });
            if (vidRes.ok) {
              const vidJson = await vidRes.json();
              if (vidJson?.data?.video?.like_count !== undefined) {
                serverLikes = parseInt(vidJson.data.video.like_count, 10);
              }
              if (vidJson?.data?.video?.user_disliked !== undefined) {
                serverDisliked = Boolean(vidJson.data.video.user_disliked);
              }
            }
          } catch (_) {}
        }

        const finalLikeCount = serverLikes !== undefined ? serverLikes : newLikeCount;
        setLikeStates((prev) => ({
          ...prev,
          [shortId]: {
            isLiked: false,
            isDisliked: serverDisliked,
            likeCount: finalLikeCount,
          },
        }));
        setShortsList((prev) =>
          prev.map((s) =>
            String(s.id) === String(shortId)
              ? {
                  ...s,
                  userLiked: false,
                  userDisliked: serverDisliked,
                  likes: finalLikeCount,
                }
              : s
          )
        );
      }

      window.dispatchEvent(
        new CustomEvent('speedotube:video-like-changed', {
          detail: {
            videoId: shortId,
            user_liked: 0,
            user_disliked: nextDisliked ? 1 : 0,
            like_count: newLikeCount,
          },
        })
      );
    } catch (err) {
      console.warn('Short dislike request error:', err);
    }
  };

  // Subscribe Toggle
  const isSubscribed = Boolean(currentShort && subscribedChannels[currentShort.channelHandle]);
  const handleToggleSubscribe = async (e) => {
    e.stopPropagation();
    if (!currentShort) return;
    if (!isUserAuthenticated) {
      openSignInPrompt('subscribe', currentShort.channelName || '');
      return;
    }
    const handle = currentShort.channelHandle;
    const nextSub = !subscribedChannels[handle];

    setSubscribedChannels((prev) => ({
      ...prev,
      [handle]: nextSub,
    }));

    const curAcc = getActiveAccount() || activeAccount;
    const curToken = curAcc?.token || getActiveUserToken(curAcc);
    const channelId = currentShort.channel_id || currentShort.channelId;
    if (channelId) {
      try {
        const endpoint = nextSub ? 'subscribe' : 'unsubscribe';
        await fetch(`${API_BASE_URL}/api/v1/subscribers/${endpoint}/channels/${channelId}`, {
          method: 'POST',
          headers: getApiHeaders(true, curToken),
        });
        window.dispatchEvent(new CustomEvent('speedotube:subscription-changed', {
          detail: { channelId, isSubscribed: nextSub },
        }));
        window.dispatchEvent(new CustomEvent('speedotube_subscription_toggled', {
          detail: { channelId, isSubscribed: nextSub },
        }));
      } catch (err) {
        console.warn('Subscription error:', err);
      }
    }
  };

  // Comments Submission
  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!isUserAuthenticated) {
      openSignInPrompt('comment', currentShort?.title || '');
      return;
    }
    if (!newCommentText.trim() || !currentShort) return;

    const commentText = newCommentText.trim();
    setIsSubmittingComment(true);

    const curAcc = getActiveAccount() || activeAccount;
    const curToken = curAcc?.token || getActiveUserToken(curAcc);
    const author_name = curAcc?.name || curAcc?.fullName || curAcc?.username || 'User';
    const handle_name = curAcc?.handle || curAcc?.handle_name || `@${(author_name).toLowerCase().replace(/[^a-z0-9]/gi, '')}`;

    const tempId = `cmt_${Date.now()}`;
    const optimisticComment = {
      id: tempId,
      text: commentText,
      comment: commentText,
      created_date: new Date().toISOString(),
      user_id: curAcc?.userId || curAcc?.id || 1,
      name: author_name,
      author_name: author_name,
      channel_name: author_name,
      handle_name: handle_name,
      avatar_url: curAcc?.avatar || curAcc?.avatar_url || '',
      avatar: curAcc?.avatar || curAcc?.avatar_url || '',
      like_count: 0,
      dislike_count: 0,
    };

    setCommentsMap((prev) => ({
      ...prev,
      [currentShort.id]: [optimisticComment, ...(prev[currentShort.id] || [])],
    }));
    setNewCommentText('');

    try {
      let headers = {
        ...getApiHeaders(true, curToken),
        'Content-Type': 'application/json',
      };
      let res = await fetch(`${API_BASE_URL}/api/v1/comments/videos/${currentShort.id}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text: commentText,
          author_name,
          handle_name,
        }),
      });

      if (res.status === 401) {
        const freshToken = curToken || getActiveUserToken(curAcc);
        if (freshToken) {
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            'Content-Type': 'application/json',
          };
          res = await fetch(`${API_BASE_URL}/api/v1/comments/videos/${currentShort.id}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              text: commentText,
              author_name,
              handle_name,
            }),
          });
        }
      }

      if (res.ok) {
        await fetchCommentsForShort(currentShort.id);
        window.dispatchEvent(
          new CustomEvent('speedotube:comment-added', {
            detail: { videoId: currentShort.id },
          })
        );
      }
    } catch (err) {
      console.warn('Failed to submit comment:', err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Comment Like / Dislike in Shorts
  const [activeReplyCommentId, setActiveReplyCommentId] = useState(null);
  const [replyTextMap, setReplyTextMap] = useState({});
  const [repliesMap, setRepliesMap] = useState({}); // { [commentId]: replies[] }
  const [expandedReplies, setExpandedReplies] = useState({});

  const handleCommentLike = async (commentId) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('like', 'this comment');
      return;
    }
    const shortId = currentShort?.id;
    if (!shortId) return;

    // Optimistically toggle like state in commentsMap
    setCommentsMap((prev) => {
      const list = prev[shortId] || [];
      return {
        ...prev,
        [shortId]: list.map((c) => {
          if (String(c.id) !== String(commentId)) return c;
          const nextLiked = !c.userLiked;
          const countDelta = nextLiked ? 1 : (c.userLiked ? -1 : 0);
          return {
            ...c,
            userLiked: nextLiked,
            userDisliked: false,
            likes: Math.max(0, (c.likes || c.like_count || 0) + countDelta),
          };
        }),
      };
    });

    try {
      const curAcc = activeAccount || getActiveAccount();
      const curToken = curAcc?.token || getActiveUserToken(curAcc);
      const res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/likes`, {
        method: 'POST',
        headers: {
          ...getApiHeaders(true, curToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ like_dislike: 'like' }),
      });
      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          const { user_liked, user_disliked, like_count } = json.data;
          setCommentsMap((prev) => {
            const list = prev[shortId] || [];
            return {
              ...prev,
              [shortId]: list.map((c) =>
                String(c.id) === String(commentId)
                  ? {
                      ...c,
                      userLiked: Boolean(user_liked),
                      userDisliked: Boolean(user_disliked),
                      likes: like_count !== undefined ? Number(like_count) : c.likes,
                    }
                  : c
              ),
            };
          });
        }
      }
    } catch (e) {
      console.warn('Shorts comment like failed:', e);
    }
  };

  const handleCommentDislike = async (commentId) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('dislike', 'this comment');
      return;
    }
    const shortId = currentShort?.id;
    if (!shortId) return;

    setCommentsMap((prev) => {
      const list = prev[shortId] || [];
      return {
        ...prev,
        [shortId]: list.map((c) => {
          if (String(c.id) !== String(commentId)) return c;
          const nextDisliked = !c.userDisliked;
          return {
            ...c,
            userLiked: false,
            userDisliked: nextDisliked,
            likes: c.userLiked ? Math.max(0, (c.likes || c.like_count || 1) - 1) : c.likes,
          };
        }),
      };
    });

    try {
      const curAcc = activeAccount || getActiveAccount();
      const curToken = curAcc?.token || getActiveUserToken(curAcc);
      const res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/likes`, {
        method: 'POST',
        headers: {
          ...getApiHeaders(true, curToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ like_dislike: 'dislike' }),
      });
      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          const { user_liked, user_disliked, like_count } = json.data;
          setCommentsMap((prev) => {
            const list = prev[shortId] || [];
            return {
              ...prev,
              [shortId]: list.map((c) =>
                String(c.id) === String(commentId)
                  ? {
                      ...c,
                      userLiked: Boolean(user_liked),
                      userDisliked: Boolean(user_disliked),
                      likes: like_count !== undefined ? Number(like_count) : c.likes,
                    }
                  : c
              ),
            };
          });
        }
      }
    } catch (e) {
      console.warn('Shorts comment dislike failed:', e);
    }
  };

  const handlePostReply = async (commentId, authorHandle) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('reply', 'this comment');
      return;
    }
    const replyText = (replyTextMap[commentId] || '').trim();
    if (!replyText || !currentShort) return;

    const curAcc = getActiveAccount() || activeAccount;
    const curToken = curAcc?.token || getActiveUserToken(curAcc);
    const author_name = curAcc?.name || curAcc?.fullName || 'User';
    const handle_name = curAcc?.handle || `@${(author_name).toLowerCase().replace(/[^a-z0-9]/gi, '')}`;

    const optimisticReply = {
      id: `rep_${Date.now()}`,
      parentId: commentId,
      author: author_name,
      handle: handle_name,
      avatar: curAcc?.avatar || curAcc?.avatar_url || '',
      text: replyText,
      likes: 0,
      dislikes: 0,
      created_date: new Date().toISOString(),
    };

    setRepliesMap((prev) => ({
      ...prev,
      [commentId]: [...(prev[commentId] || []), optimisticReply],
    }));
    setExpandedReplies((prev) => ({ ...prev, [commentId]: true }));
    setReplyTextMap((prev) => ({ ...prev, [commentId]: '' }));
    setActiveReplyCommentId(null);

    try {
      await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/reply`, {
        method: 'POST',
        headers: {
          ...getApiHeaders(true, curToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: replyText,
          author_name,
          handle_name,
          video_id: currentShort.id,
        }),
      });
    } catch (err) {
      console.warn('Failed to submit reply:', err);
    }
  };

  const commentsList = (currentShort && commentsMap[currentShort.id]) || [];
  const displayTime = isScrubbing && scrubPreviewTime !== null ? scrubPreviewTime : currentTime;
  const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0;

  return (
    <div
      className={`yt-shorts-page ${isFullscreen ? 'yt-shorts-fullscreen-mode' : ''}`}
      ref={containerRef}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Fullscreen Video Details on Left Side of the Screen (Matching YouTube Reference) */}
      {isFullscreen && currentShort && (
        <div
          className={`yt-shorts-fullscreen-left-details ${isCommentsOpen ? 'is-faded-out' : 'is-visible'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="yt-shorts-channel-row">
            <div
              className="yt-shorts-channel-avatar"
              style={{
                backgroundColor: getAvatarColor(currentShort.channelName || 'C'),
              }}
              onClick={() => {
                if (onNavigate && currentShort.channelHandle) {
                  onNavigate(`/channel/${encodeURIComponent(currentShort.channelHandle)}`);
                }
              }}
            >
              {currentShort.avatarUrl ? (
                <img src={currentShort.avatarUrl} alt={currentShort.channelName} />
              ) : (
                <span>{(currentShort.channelName || 'C').charAt(0).toUpperCase()}</span>
              )}
            </div>

            <span
              className="yt-shorts-channel-handle"
              onClick={() => {
                if (onNavigate && currentShort.channelHandle) {
                  onNavigate(`/channel/${encodeURIComponent(currentShort.channelHandle)}`);
                }
              }}
            >
              {currentShort.channelHandle}
            </span>

            {!isOwner && (
              <button
                type="button"
                className={`yt-shorts-sub-btn ${isSubscribed ? 'subscribed' : ''}`}
                onClick={handleToggleSubscribe}
              >
                {isSubscribed ? (
                  <>
                    <MdCheck size={14} /> Subscribed
                  </>
                ) : (
                  'Subscribe'
                )}
              </button>
            )}
          </div>

          <h1 className="yt-shorts-fullscreen-title">{currentShort.title}</h1>
        </div>
      )}

      <div className="yt-shorts-layout-wrapper">
        {/* Main Center Video Card with Floating Features */}
        <div className={`yt-shorts-player-card ${isFullscreen ? 'yt-fullscreen-active' : ''}`} ref={playerCardRef}>
          <div
            className={`yt-shorts-player-container ${bounceClass}`}
            onClick={handleTogglePlay}
          >
            {/* Outgoing Slide during Swipe Transition */}
            {transitionState && (
              <div className={`yt-shorts-slide outgoing-${transitionState.direction}`}>
                {transitionState.outgoingShort?.thumbnailUrl ? (
                  <img
                    src={transitionState.outgoingShort.thumbnailUrl}
                    alt=""
                    className="yt-shorts-video-element yt-shorts-poster-fill"
                  />
                ) : (
                  <div className="yt-shorts-video-element yt-shorts-fallback-bg" />
                )}
                <div className="yt-shorts-info-overlay" onClick={(e) => e.stopPropagation()}>
                  <div className="yt-shorts-channel-row">
                    <div
                      className="yt-shorts-channel-avatar"
                      style={{
                        backgroundColor: getAvatarColor(transitionState.outgoingShort?.channelName || 'C'),
                      }}
                    >
                      {transitionState.outgoingShort?.avatarUrl ? (
                        <img src={transitionState.outgoingShort.avatarUrl} alt="" />
                      ) : (
                        <span>{(transitionState.outgoingShort?.channelName || 'C').charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <span className="yt-shorts-channel-handle">
                      {transitionState.outgoingShort?.channelHandle}
                    </span>
                  </div>
                  <h1 className="yt-shorts-title-text">{transitionState.outgoingShort?.title}</h1>
                </div>
              </div>
            )}

            {/* Active / Incoming Slide */}
            <div
              className={`yt-shorts-slide ${
                transitionState ? `incoming-${transitionState.direction}` : 'active-slide'
              }`}
            >
              {/* The Vertical Short Video */}
              {currentShort && (
                <video
                  ref={videoRef}
                  key={currentShort.id}
                  poster={currentShort.thumbnailUrl}
                  className="yt-shorts-video-element"
                  loop={!isAutoscroll}
                  playsInline
                  muted={isMuted}
                  onCanPlay={(e) => {
                    // Clamp rate only. play() is handled exclusively by the play/pause useEffect.
                    e.target.playbackRate = 1.0;
                    e.target.defaultPlaybackRate = 1.0;
                  }}
                  onPlay={(e) => {
                    if (e?.target) {
                      e.target.playbackRate = 1.0;
                      e.target.defaultPlaybackRate = 1.0;
                    }
                    handlePlay();
                  }}
                  onPause={handlePause}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={() => {
                    if (isAutoscroll && !isTransitioningRef.current) {
                      handleNextShort();
                    }
                  }}
                  onLoadedMetadata={(e) => {
                    if (e?.target) {
                      e.target.playbackRate = 1.0;
                      e.target.defaultPlaybackRate = 1.0;
                    }
                    handleTimeUpdate();
                  }}
                  onRateChange={(e) => {
                    if (e?.target && e.target.playbackRate !== 1.0) {
                      e.target.playbackRate = 1.0;
                    }
                  }}
                />
              )}

              {/* Closed Captions Live Overlay when toggled ON */}
              {isCaptionsOn && currentShort && (
                <div className="yt-shorts-live-cc-badge" onClick={(e) => e.stopPropagation()}>
                  <span>{currentShort.title || 'English [Auto-generated]'}</span>
                </div>
              )}

              {/* Bottom-Left Information Overlay (Faded by default, clear on hover) */}
              <div className="yt-shorts-info-overlay" onClick={(e) => e.stopPropagation()}>
                {currentShort && (
                  <>
                    {/* Channel Details & Subscribe Pill Button */}
                    <div className="yt-shorts-channel-row">
                      <div
                        className="yt-shorts-channel-avatar"
                        style={{
                          backgroundColor: getAvatarColor(currentShort.channelName || 'C'),
                        }}
                        onClick={() => {
                          if (onNavigate && currentShort.channelHandle) {
                            onNavigate(`/channel/${encodeURIComponent(currentShort.channelHandle)}`);
                          }
                        }}
                      >
                        {currentShort.avatarUrl ? (
                          <img src={currentShort.avatarUrl} alt={currentShort.channelName} />
                        ) : (
                          <span>{(currentShort.channelName || 'C').charAt(0).toUpperCase()}</span>
                        )}
                      </div>

                      <span
                        className="yt-shorts-channel-handle"
                        onClick={() => {
                          if (onNavigate && currentShort.channelHandle) {
                            onNavigate(`/channel/${encodeURIComponent(currentShort.channelHandle)}`);
                          }
                        }}
                      >
                        {currentShort.channelHandle}
                      </span>

                      {!isOwner && (
                        <button
                          type="button"
                          className={`yt-shorts-sub-btn ${isSubscribed ? 'subscribed' : ''}`}
                          onClick={handleToggleSubscribe}
                        >
                          {isSubscribed ? (
                            <>
                              <MdCheck size={14} /> Subscribed
                            </>
                          ) : (
                            'Subscribe'
                          )}
                        </button>
                      )}
                    </div>

                    {/* Short Title / Name (No clutter, pure name) */}
                    <h1 className="yt-shorts-title-text">{currentShort.title}</h1>
                  </>
                )}
              </div>
            </div>

            {/* Top Floating Controls Bar */}
            <div className="yt-shorts-top-bar" onClick={(e) => e.stopPropagation()}>
              <div className="yt-shorts-top-left">
                {/* Floating Play / Pause Button */}
                <button
                  type="button"
                  className="yt-shorts-floating-btn"
                  onClick={handleTogglePlay}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  data-yt-tooltip={isPlaying ? 'Pause' : 'Play'}
                  data-tooltip-pos="bottom"
                >
                  {isPlaying ? <MdPause size={22} /> : <MdPlayArrow size={24} />}
                </button>

                {/* Floating Mute / Unmute Button */}
                <button
                  type="button"
                  className="yt-shorts-floating-btn"
                  onClick={handleToggleMute}
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                  data-yt-tooltip={isMuted ? 'Unmute' : 'Mute'}
                  data-tooltip-pos="bottom"
                >
                  {isMuted ? <MdVolumeOff size={21} /> : <MdVolumeUp size={21} />}
                </button>
              </div>

              {/* YouTube Shorts Top-Right Controls Capsule: Exactly Two Toggles (Auto Scroll & Full Screen) */}
              <div className="yt-shorts-top-right">
                <div className="yt-shorts-capsule-group">
                  {/* 1. Auto-scroll Mode Toggle Button */}
                  <button
                    type="button"
                    className={`yt-shorts-capsule-btn ${isAutoscroll ? 'active-toggle' : ''}`}
                    onClick={handleToggleAutoscroll}
                    aria-label={isAutoscroll ? 'Auto-scroll (On)' : 'Auto-scroll (Off)'}
                  >
                    <MdAutorenew size={21} className={isAutoscroll ? 'yt-spin-slow' : ''} />
                  </button>

                  {/* 2. Full Screen Toggle Button */}
                  <button
                    type="button"
                    className={`yt-shorts-capsule-btn ${isFullscreen ? 'active-toggle' : ''}`}
                    onClick={handleToggleFullscreen}
                    aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
                  >
                    {isFullscreen ? <MdFullscreenExit size={22} /> : <MdFullscreen size={22} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Center Animated Pop Play/Pause/Mute Feedback Indicator */}
            {centerIcon && (
              <div className="yt-shorts-center-feedback-badge">
                {centerIcon === 'play' && <MdPlayArrow size={42} />}
                {centerIcon === 'pause' && <MdPause size={42} />}
                {centerIcon === 'volume-up' && <MdVolumeUp size={40} />}
                {centerIcon === 'volume-off' && <MdVolumeOff size={40} />}
                {centerIcon === 'fullscreen' && <MdFullscreen size={42} />}
                {centerIcon === 'fullscreen-exit' && <MdFullscreenExit size={42} />}
                {centerIcon === 'autoscroll-on' && <MdAutorenew size={40} />}
                {centerIcon === 'autoscroll-off' && <MdAutorenew size={40} />}
                {centerIcon === 'captions-on' && <MdSubtitles size={40} />}
                {centerIcon === 'captions-off' && <MdOutlineSubtitles size={40} />}
              </div>
            )}

            {/* Bottom Playback Scrubber Bar */}
            <div
              ref={scrubberTrackRef}
              className={`yt-shorts-scrubber-track ${isScrubbing ? 'is-scrubbing' : ''}`}
              onPointerDown={handleScrubberPointerDown}
              onPointerMove={handleScrubberPointerMove}
              onPointerUp={handleScrubberPointerUp}
              onPointerCancel={handleScrubberPointerUp}
            >
              <div
                className="yt-shorts-scrubber-progress"
                style={{ width: `${progressPercent}%` }}
              >
                <div className={`yt-shorts-scrubber-thumb ${isScrubbing ? 'active' : ''}`} />
              </div>
            </div>
          </div>

          {/* Right-Side Attached Action Buttons Column (Like, Dislike, Comments, Share, Report) */}
          <div className="yt-shorts-actions-column">
            {/* Like Button */}
            <div className="yt-shorts-action-group">
              <button
                type="button"
                className={`yt-shorts-action-btn ${currentLikeState.isLiked ? 'active-liked' : ''}`}
                onClick={handleLike}
                aria-label="I like this"
                data-yt-tooltip="I like this"
                data-tooltip-pos="left"
              >
                {currentLikeState.isLiked ? (
                  <MdThumbUp size={23} className="yt-thumb-filled" />
                ) : (
                  <MdOutlineThumbUp size={23} />
                )}
              </button>
              <span className="yt-shorts-action-count">
                {currentLikeState.likeCount > 0 ? currentLikeState.likeCount : 'Like'}
              </span>
            </div>

            {/* Dislike Button */}
            <div className="yt-shorts-action-group">
              <button
                type="button"
                className={`yt-shorts-action-btn ${currentLikeState.isDisliked ? 'active-disliked' : ''}`}
                onClick={handleDislike}
                aria-label="I dislike this"
                data-yt-tooltip="I dislike this"
                data-tooltip-pos="left"
              >
                {currentLikeState.isDisliked ? (
                  <MdThumbDown size={23} className="yt-thumb-filled" />
                ) : (
                  <MdOutlineThumbDown size={23} />
                )}
              </button>
              <span className="yt-shorts-action-count">Dislike</span>
            </div>

            {/* Comments Button */}
            <div className="yt-shorts-action-group">
              <button
                type="button"
                className={`yt-shorts-action-btn ${isCommentsOpen ? 'active-comments' : ''}`}
                onClick={() => setIsCommentsOpen((prev) => !prev)}
                aria-label="Comments"
                data-yt-tooltip="Comments"
                data-tooltip-pos="left"
              >
                <BiCommentDetail size={23} />
              </button>
              <span className="yt-shorts-action-count">
                {commentsList.length > 0 ? commentsList.length : currentShort?.commentCount || '0'}
              </span>
            </div>

            {/* Share Button */}
            <div className="yt-shorts-action-group">
              <button
                type="button"
                className="yt-shorts-action-btn"
                onClick={() => setIsShareModalOpen(true)}
                aria-label="Share"
                data-yt-tooltip="Share"
                data-tooltip-pos="left"
              >
                <RiShareForwardLine size={24} />
              </button>
              <span className="yt-shorts-action-count">Share</span>
            </div>
          </div>
        </div>


        {/* Slide-in Comments Drawer (Right Side) with Smooth Open/Close Transitions */}
        <div
          className={`yt-shorts-comments-panel ${isCommentsOpen ? 'is-open' : 'is-closed'} ${!isUserAuthenticated ? 'is-blurred' : ''}`}
          aria-hidden={!isCommentsOpen}
        >
          <div className="yt-shorts-comments-inner">
            <div className="yt-shorts-comments-header">
              <div className="yt-shorts-comments-title">
                <h3>Comments</h3>
                <span className="yt-shorts-comments-count-pill">{commentsList.length}</span>
              </div>
              <button
                type="button"
                className="yt-shorts-close-btn"
                onClick={() => setIsCommentsOpen(false)}
                aria-label="Close"
                data-yt-tooltip="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            {/* Blurred Guest / Logged-Out Overlay with Sign In Callout */}
            {!isUserAuthenticated && (
              <div
                className="yt-shorts-comments-blur-overlay"
                onClick={() => openSignInPrompt('comment', currentShort?.title || '')}
              >
                <div className="yt-shorts-comments-blur-card" onClick={(e) => e.stopPropagation()}>
                  <div className="yt-shorts-comments-blur-icon">
                    <HiOutlineUserCircle size={40} />
                  </div>
                  <h4 className="yt-shorts-comments-blur-title">Want to join the conversation?</h4>
                  <p className="yt-shorts-comments-blur-desc">
                    Sign in to comment, reply, and interact with creators on Shorts.
                  </p>
                  <button
                    type="button"
                    className="yt-shorts-comments-blur-signin-btn"
                    onClick={handlePromptSignIn}
                  >
                    Sign in
                  </button>
                </div>
              </div>
            )}

            {/* Comments List */}
            <div className="yt-shorts-comments-body">
              {commentsList.length === 0 ? (
                <div className="yt-shorts-no-comments">
                  <BiCommentDetail size={40} style={{ opacity: 0.4, marginBottom: '10px' }} />
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                commentsList.map((cmt) => {
                  const author = cmt.author_name || cmt.name || 'User';
                  const handle = cmt.handle_name ? (cmt.handle_name.startsWith('@') ? cmt.handle_name : `@${cmt.handle_name}`) : `@${author.toLowerCase().replace(/[^a-z0-9]/gi, '')}`;
                  const avatar = cmt.avatar_url || cmt.avatar;
                  const text = cmt.text || cmt.comment || '';
                  return (
                    <div key={cmt.id} className="yt-shorts-comment-item">
                      <div
                        className="yt-shorts-comment-avatar"
                        style={{ backgroundColor: getAvatarColor(author) }}
                      >
                        {avatar ? (
                          <img src={avatar} alt={author} />
                        ) : (
                          <span>{author.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="yt-shorts-comment-content">
                        <div className="yt-shorts-comment-meta">
                          <span className="yt-shorts-comment-author">{handle}</span>
                          <span className="yt-shorts-comment-time">
                            {cmt.created_date ? new Date(cmt.created_date).toLocaleDateString() : 'Just now'}
                          </span>
                        </div>
                        <p className="yt-shorts-comment-text">{text}</p>
                        <div className="yt-shorts-comment-actions">
                          <button
                            type="button"
                            className={`yt-shorts-cmt-act-btn ${cmt.userLiked ? 'active' : ''}`}
                            onClick={() => handleCommentLike(cmt.id)}
                            aria-label="Like"
                          >
                            {cmt.userLiked ? <MdThumbUp size={14} /> : <MdOutlineThumbUp size={14} />}
                            {(cmt.likes || cmt.like_count || 0) > 0 && (
                              <span>{cmt.likes || cmt.like_count}</span>
                            )}
                          </button>
                          <button
                            type="button"
                            className={`yt-shorts-cmt-act-btn ${cmt.userDisliked ? 'active' : ''}`}
                            onClick={() => handleCommentDislike(cmt.id)}
                            aria-label="Dislike"
                          >
                            {cmt.userDisliked ? <MdThumbDown size={14} /> : <MdOutlineThumbDown size={14} />}
                          </button>
                          <button
                            type="button"
                            className="yt-shorts-cmt-reply-btn"
                            onClick={() => setActiveReplyCommentId((prev) => (prev === cmt.id ? null : cmt.id))}
                          >
                            Reply
                          </button>
                        </div>

                        {/* Inline Reply Input for Shorts */}
                        {activeReplyCommentId === cmt.id && (
                          <div className="yt-shorts-reply-input-row">
                            <input
                              type="text"
                              value={replyTextMap[cmt.id] || ''}
                              onChange={(e) => setReplyTextMap((prev) => ({ ...prev, [cmt.id]: e.target.value }))}
                              placeholder={`Reply to ${handle}...`}
                              className="yt-shorts-reply-input"
                              autoFocus
                            />
                            <button
                              type="button"
                              className="yt-shorts-reply-send-btn"
                              onClick={() => handlePostReply(cmt.id, handle)}
                              disabled={!(replyTextMap[cmt.id] || '').trim()}
                            >
                              Reply
                            </button>
                          </div>
                        )}

                        {/* Nested Replies List */}
                        {repliesMap[cmt.id] && repliesMap[cmt.id].length > 0 && (
                          <div className="yt-shorts-replies-list">
                            {repliesMap[cmt.id].map((rep) => (
                              <div key={rep.id} className="yt-shorts-reply-item">
                                <span className="yt-shorts-reply-author">{rep.handle || rep.author}</span>
                                <span className="yt-shorts-reply-text">{rep.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* New Comment Input Box */}
            <form className="yt-shorts-comment-input-box" onSubmit={handlePostComment}>
              <div
                className="yt-shorts-comment-avatar self"
                style={{ backgroundColor: getAvatarColor(activeAccount.name || 'U') }}
              >
                <span>{(activeAccount.name || 'U').charAt(0).toUpperCase()}</span>
              </div>
              <input
                type="text"
                value={newCommentText}
                readOnly={!isUserAuthenticated}
                onChange={(e) => isUserAuthenticated && setNewCommentText(e.target.value)}
                onFocus={() => {
                  if (!isUserAuthenticated) {
                    openSignInPrompt('comment', currentShort?.title || '');
                  }
                }}
                placeholder={isUserAuthenticated ? "Add a comment..." : "Sign in to comment..."}
                className="yt-shorts-comment-input"
              />
              <button
                type="submit"
                disabled={!newCommentText.trim() || isSubmittingComment}
                className="yt-shorts-comment-send-btn"
                aria-label="Send Comment"
              >
                <MdSend size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Share Modal Dialog */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        videoData={currentShort}
        videoUrlId={currentShort?.videoUrlId || currentShort?.id}
      />

      {/* Report Modal Dialog */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        videoData={currentShort}
        currentTime={currentTime}
      />

      {/* Smooth Sign-In Prompt Modal for Logged-Out Interactivity */}
      <SignInPromptModal
        isOpen={signInPrompt.isOpen}
        action={signInPrompt.action}
        targetName={signInPrompt.targetName}
        isShort={true}
        onSignIn={handlePromptSignIn}
        onClose={closeSignInPrompt}
      />
    </div>
  );
}
